import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../Services/Store";
import styles from "./TaskManagement.module.css";
import { FaSearch, FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import AddTaskPanel from "./AddTaskPanel";
import TaskDetailPanel from "./TaskDetailPanel";
import TaskCountBoxes, { TaskStatusCounts } from "./TaskCountBoxes";
import WebService from "../Services/WebService";
import Paginations from "../Customs/pagination/Paginations";
import NoDataAvailable from "../Customs/NoDataAvailable/NoDataAvailable";
import { TaskItem, AccessItem } from "../Models/Models";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

// UI statuses (matches what your dropdown shows)
const STATUSES = ["All Status", "Pending", "Processing", "Paused", "Completed"] as const;
const TASKS_PER_PAGE = 10;

// Map UI label to backend value if needed
const statusMap: Record<string, string> = {
  "All Status": "",
  "Pending": "PENDING",
  "Processing": "PROCESSING",
  "Paused": "PAUSED",
  "Completed": "COMPLETED",
};

const statusLabelMap: Record<string, string> = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  IN_PROGRESS: "Processing",
  IN_PROCESSING: "Processing",
  PAUSED: "Paused",
  COMPLETED: "Completed",
  CANCELED: "Canceled",
  CANCELLED: "Canceled",
};

const taskStatusClass = (status: string | undefined, css: Record<string, string>) => {
  const key = (status || "PENDING").toUpperCase().replace(/\s/g, "_");
  if (key === "PROCESSING" || key === "IN_PROGRESS" || key === "IN_PROCESSING") {
    return css.statusInProgress || css.statusProcessing;
  }
  if (key === "PAUSED") return css.statusPaused;
  if (key === "COMPLETED") return css.statusCompleted;
  if (key === "PENDING") return css.statusPending;
  return css[`status${key}`];
};

const TaskManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user_id } = useParams<{ user_id?: string }>();

  // RBAC permissions
  const accessList = useSelector((state: RootState) => state.auth.access as AccessItem[] || []);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserId = currentUser?.user_id;
  const isAdmin = (currentUser?.user_type || "").toUpperCase() === "ADMIN";
  const moduleAccess = accessList.find(item => item.code === "task_management");
  const hasReadAccess = !!moduleAccess?.read;
  const canWrite = !!moduleAccess?.write;
  const canUpdate = !!moduleAccess?.update;
  const canDelete = !!moduleAccess?.delete;
  const canModify = canWrite || canUpdate;

  // All state/hooks must be BEFORE any return/condition!
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTaskId, setSearchTaskId] = useState("");
  const [searchTaskTitle, setSearchTaskTitle] = useState("");
  const [searchEmployee, setSearchEmployee] = useState("");
  const [statuses, setStatus] = useState<typeof STATUSES[number]>("All Status");
  const [statusCounts, setStatusCounts] = useState<TaskStatusCounts | null>(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [detailMode, setDetailMode] = useState<"view" | "edit">("view");
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [totalTasks, setTotalTasks] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [employeeName, setEmployeeName] = useState("");
  const [statusOnlyEdit, setStatusOnlyEdit] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const fetchingRef = useRef(false);
  const isMyTasks = !isAdmin;

  // Deep link from a notification: /task-management?task=<id> opens that task
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkTaskId = searchParams.get("task");
  useEffect(() => {
    const id = Number(deepLinkTaskId);
    if (!deepLinkTaskId || !Number.isFinite(id) || id <= 0) return;
    setSelectedTaskId(id);
    setDetailMode("view");
    setStatusOnlyEdit(false);
    setShowDetailPanel(true);
    searchParams.delete("task");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTaskId]);

  const fetchTasks = useCallback((pageToLoad = 1, append = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setHasMore(true);
    }

    let apiUrl = `/v1/door-management/tasks?page_number=${pageToLoad}&limit=${TASKS_PER_PAGE}`;

    if (user_id) {
      apiUrl += `&user_id=${user_id}`;
    } else if (!isAdmin && currentUserId) {
      apiUrl += `&user_id=${currentUserId}`;
    }
    if (searchTaskId) {
      apiUrl += `&task_id=${encodeURIComponent(searchTaskId)}`;
    }
    if (searchTaskTitle) {
      apiUrl += `&task_like=${encodeURIComponent(searchTaskTitle)}&search=${encodeURIComponent(searchTaskTitle)}`;
    }
    if (searchEmployee.length >= 3) {
      apiUrl += `&assigned_to=${encodeURIComponent(searchEmployee)}`;
    }
    if (statuses !== "All Status" && statusMap[statuses]) {
      apiUrl += `&status=${encodeURIComponent(statusMap[statuses])}&statuses=${encodeURIComponent(statusMap[statuses])}`;
    }

    WebService.getAPI({
      action: apiUrl,
      type: "",
    })
      .then((res: any) => {
        const incoming: TaskItem[] = Array.isArray(res?.data?.tasks) ? res.data.tasks : [];
        const total = res?.data?.pagination?.total_records ?? incoming.length;
        const totalPages = res?.data?.pagination?.total_pages ?? 1;
        setTotalTasks(total);
        if (res?.data?.status_counts) setStatusCounts(res.data.status_counts);
        setCurrentPage(pageToLoad);
        if (append) {
          setTasks((prev) => {
            const seen = new Set(prev.map((t) => String(t.id ?? t.task_id)));
            return [...prev, ...incoming.filter((t) => !seen.has(String(t.id ?? t.task_id)))];
          });
        } else {
          setTasks(incoming);
        }
        setHasMore(pageToLoad < totalPages && incoming.length > 0);
      })
      .catch(() => {
        if (!append) {
          setTasks([]);
          setTotalTasks(0);
        }
        setHasMore(false);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
        fetchingRef.current = false;
      });
  }, [user_id, isAdmin, currentUserId, searchTaskId, searchTaskTitle, searchEmployee, statuses]);

  useEffect(() => {
    if (!isAdmin && currentUserId) {
      const mine = String(currentUserId);
      if (user_id !== mine) {
        navigate(`/task-management/${mine}`, { replace: true });
      }
    }
  }, [isAdmin, currentUserId, user_id, navigate]);

  useEffect(() => {
    if (isMyTasks) {
      fetchTasks(1, false);
    }
  }, [fetchTasks, isMyTasks]);

  useEffect(() => {
    if (!isMyTasks) {
      fetchTasks(currentPage, false);
    }
    // Filter changes rebuild fetchTasks and should load page 1 via the setter below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isMyTasks, fetchTasks]);

  useEffect(() => {
    if (!isMyTasks) return;
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasMore &&
          !loading &&
          !loadingMore &&
          tasks.length > 0
        ) {
          fetchTasks(currentPage + 1, true);
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMyTasks, hasMore, loading, loadingMore, currentPage, fetchTasks, tasks.length]);

  useEffect(() => {
    if (!user_id) {
      setEmployeeName("");
      return;
    }

    WebService.getAPI<any>({
      action: `/v1/door-management/users?user_id=${user_id}`,
    })
      .then((res) => {
        const arr = Array.isArray(res?.data?.users) ? res.data.users : [];
        if (arr.length > 0) {
          setEmployeeName(arr[0].username || "");
        } else {
          setEmployeeName("");
        }
      })
      .catch(() => setEmployeeName(""));
  }, [user_id]);

  const refreshTaskList = useCallback(() => {
    fetchTasks(isMyTasks ? 1 : currentPage, false);
  }, [fetchTasks, isMyTasks, currentPage]);

  if (!hasReadAccess) {
    return (
      <div style={{ padding: "32px", textAlign: "center", color: "#d32f2f", fontWeight: 700, fontSize: 22 }}>
        Access Denied: You do not have permission to view tasks.
      </div>
    );
  }

  const assignedToMe = (task: TaskItem) =>
    currentUserId != null && Number(task.assign_to) === Number(currentUserId);
  const canEditTask = (task: TaskItem) => (isAdmin && canModify) || assignedToMe(task);

  // Open view/edit
  const openDetailPanel = (task: TaskItem, mode: "view" | "edit") => {
    setSelectedTaskId(task.id || null);
    setDetailMode(mode);
    setStatusOnlyEdit(mode === "edit" && !isAdmin);
    setShowDetailPanel(true);
  };

  // DELETE TASK 
  const handleDelete = async (taskId: string | undefined) => {

    if (!(canDelete || canModify) || !taskId) return;
    const idNum = Number(String(taskId).replace(/\D+/g, ''));
    if (!idNum || isNaN(idNum)) {
      toast.error("Invalid Task ID");
      return;
    }
    const confirm = await Swal.fire({
      title: "Delete Task",
      text: "Are you sure you want to delete this task?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: styles.swirlConfirmDelete,
        cancelButton: styles.swirlCancelDelete,
      },
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      await WebService.deleteAPI({
        action: `/v1/door-management/tasks/${idNum}`,
        type: "",
      });
      toast.success("Task deleted successfully");
      setTasks((prev) => prev.filter((t) => t.task_id !== taskId && String(t.id) !== String(idNum)));
      setTotalTasks((prev) => Math.max(0, prev - 1));
      if (!isMyTasks) {
        const tasksLeftOnPage = tasks.length - 1;
        const isLastPageAndEmpty = tasksLeftOnPage === 0 && currentPage > 1;
        if (isLastPageAndEmpty) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchTasks(currentPage, false);
        }
      }
    } catch (err) {
      toast.error("Failed to delete task");
    }
  };


  return (
    <div className={styles.pageWrapper}>
      <h2 style={{ fontWeight: 700, marginBottom: 24 }}>
        {user_id
          ? (isAdmin ? `Tasks for Employee ${employeeName || user_id}` : "My Tasks")
          : "All Tasks"}
      </h2>
      <TaskCountBoxes
        counts={statusCounts}
        active={statuses}
        onSelect={(filter) => {
          setStatus(filter as typeof STATUSES[number]);
          setCurrentPage(1);
        }}
      />
      <div className={styles.filterRow}>
        <div style={{ display: "flex", gap: 13, alignItems: "center", flex: 1 }}>
          <div className={styles.inputGroup}>
            <FaSearch className={styles.inputIcon} />
<input
  type="text"
  className={styles.input}
  placeholder="Task-ID Number"
  value={searchTaskId}
  inputMode="numeric"
  pattern="[0-9]*"
  onChange={e => {
    // Remove all non-digit chars
    const onlyNumbers = e.target.value.replace(/\D/g, "");
    setSearchTaskId(onlyNumbers);
    setCurrentPage(1);
  }}
/>

          </div>
          <div className={styles.inputGroup}>
            <FaSearch className={styles.inputIcon} />
            <input
              type="text"
              className={styles.input}
              placeholder="Search Task Title..."
              value={searchTaskTitle}
              onChange={(e) => { setSearchTaskTitle(e.target.value); setCurrentPage(1); }}
            />
          </div>
          {isAdmin && (
          <div className={styles.inputGroup}>
            <FaSearch className={styles.inputIcon} />
            <input
              type="text"
              className={styles.input}
              placeholder="Search Employee..."
              value={searchEmployee}
              onChange={(e) => { setSearchEmployee(e.target.value); setCurrentPage(1); }}
              disabled={!!user_id}
            />
          </div>
          )}
  <div className={styles.inputGroup}>
    <select
      className={styles.select}
      value={statuses}
      onChange={(e) => { setStatus(e.target.value as typeof STATUSES[number]); setCurrentPage(1); }}
    >
      {STATUSES.map((stat) => (
        <option key={stat}>{stat}</option>
      ))}
    </select>
  </div>
        </div>
        {/* Only allow create if user has write/update */}
        {(isAdmin && canModify && !user_id) && (
          <button
            className={styles.createBtn}
            style={{
              marginLeft: "auto",
              marginBottom: 0,
              marginRight: 0,
              marginTop: 0,
              minWidth: 155,
            }}
            onClick={() => setShowAddTask(true)}
          >
            <MdAdd style={{ marginRight: 7, fontSize: 20 }} />
            Create Tickets
          </button>
        )}
      </div>

      {/* === TABLE === */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Task ID</th>
              <th>Title</th>
              <th>Assigned To</th>
              <th>Priority</th>
              <th>Status</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && tasks.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 40 }}>
                  <NoDataAvailable
                    message="No Tasks Found"
                    isShowLoader={true}
                    isShowNoDataAvailable={false}
                  />
                </td>
              </tr>
            ) : tasks.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 40 }}>
                  <NoDataAvailable
                    message="No Tasks Found"
                    isShowLoader={loading}
                    isShowNoDataAvailable={!loading}
                  />
                </td>
              </tr>
            ) : (
              tasks.map((task, i) => (
                <tr key={task.task_id || i}>
                  <td>{task.task_id || "--"}</td>
                  <td
                    className={styles.titleCell}
                    onClick={() => openDetailPanel(task, "view")}
                  >
                    {task.subject || task.title || "--"}
                  </td>
                  <td>
                    <span>{task.assign_to_username || "--"}</span>
                  </td>
                  <td>
                    <span
                      className={styles[
                        `priority${(task.priority || "High").replace(/\s/g, "")}`
                      ]}
                    >
                      {task.priority || "--"}
                    </span>
                  </td>
                  <td>
                <span
  className={taskStatusClass(task.status, styles as unknown as Record<string, string>)}
>
  {statusLabelMap[task.status as keyof typeof statusLabelMap] || task.status || "--"}
</span>

                  </td>
                  <td>
                    <div style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 16,
                      minHeight: 44 
                    }}>
                      <FaEye
                        className={styles.actionIcon}
                        title="View"
                        onClick={() => openDetailPanel(task, "view")}
                      />
                      {canEditTask(task) && (
                        <FaEdit
                          className={styles.actionIconEdit}
                          title={isAdmin ? "Edit" : "Update status"}
                          onClick={() => openDetailPanel(task, "edit")}
                        />
                      )}
                      {isAdmin && (canDelete || canModify) && (
                        <FaTrash
                          className={styles.actionIcon}
                          title="Delete"
                          style={{ color: "#dc2626", marginLeft: 10, cursor: "pointer" }}
                          onClick={() => handleDelete(task.task_id)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {isMyTasks ? (
          <>
            <div ref={loadMoreRef} className={styles.infiniteScrollSentinel} aria-hidden />
            {loadingMore && (
              <div className={styles.infiniteScrollLoading}>Loading more tasks...</div>
            )}
            {!loading && !loadingMore && !hasMore && tasks.length > 0 && (
              <div className={styles.infiniteScrollEnd}>
                Showing all {totalTasks || tasks.length} tasks
              </div>
            )}
            {(hasMore || loadingMore) && tasks.length > 0 && (
              <div className={styles.entriesNote}>
                {`Showing ${tasks.length} of ${totalTasks} entries`}
              </div>
            )}
          </>
        ) : (
          <>
            {!loading && tasks.length > 0 && (
              <Paginations
                totalItems={totalTasks}
                itemsCountPerPage={TASKS_PER_PAGE}
                changePage={setCurrentPage}
                changeCurrentPage={currentPage}
              />
            )}
            <div className={styles.entriesNote}>
              {`Showing ${tasks.length} of ${totalTasks} entries`}
            </div>
          </>
        )}
      </div>

<AddTaskPanel
  open={showAddTask}
  onClose={() => {
    setShowAddTask(false);
    fetchTasks(isMyTasks ? 1 : currentPage, false);
  }}
  canWrite={canModify}
/>

{showDetailPanel && selectedTaskId !== null && (
  <TaskDetailPanel
    open={showDetailPanel}
    taskId={selectedTaskId}
    mode={detailMode}
    onClose={() => {
      setShowDetailPanel(false);
      setSelectedTaskId(null);
      setStatusOnlyEdit(false);
    }}
    refreshTasks={refreshTaskList}
    canWrite={canModify && isAdmin}
    canRead={hasReadAccess}
    statusOnly={statusOnlyEdit}
  />
)}

    </div>
  );
};

export default TaskManagement;
